import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { DeviceDeactivatePayload, DeviceIssue } from './device-activation.type';
import { DeviceShipment } from './device-shipment.type';
import { CurrentAssignmentResponse, Device } from './device.type';

/**
 * The device fields a bulk edit may set. Department is the only one today;
 * a later field is a key here plus a control on the bulk bar, with no new
 * endpoint and no new service method.
 */
export interface DeviceBulkPatch {
  departmentId?: number;
}

/**
 * What a bulk edit did. `notFoundIds` covers rows deleted since the table was
 * last fetched — reported rather than treated as a failure, so a stale page does
 * not lose the changes that did go through.
 */
export interface DeviceBulkUpdateResult {
  updated: number;
  unchanged: number;
  notFoundIds: number[];
}

@Injectable({
  providedIn: 'root',
})
export class DeviceService extends ApiService {
  public eventEmitter = new EventEmitter();
  public getDevice(param): Observable<any> {
    return this.request({
      path: 'devices/',
      method: 'GET',
      query: param,
    });
  }

  public getDeviceDropDown(param): Observable<any> {
    return this.request({
      path: 'devices/dropdown',
      method: 'GET',
      query: param,
    });
  }

  /** Global aggregate counts for the Devices stat cards (respects filters). */
  public getDeviceStats(param): Observable<any> {
    return this.request({
      path: 'devices/stats',
      method: 'GET',
      query: param,
    });
  }

  public getDeviceCurrentStatus(deviceId): Observable<CurrentAssignmentResponse> {
    return this.request({
      path: `devices/current-tests/`,
      method: 'GET',
      query: {deviceId}
    });
  }

  public deleteDevice(id): Observable<any> {
    return this.request({
      path: `devices/${id}`,
      method: 'DELETE',
    });
  }

  public updateDevice(data, id): Observable<any> {
    return this.request({
      path: `devices/${id}`,
      method: 'PUT',
      body: data,
    });
  }

  public patchDevice(data, id): Observable<any> {
    return this.request({
      path: `devices/${id}`,
      method: 'PATCH',
      body: data,
    });
  }

  /**
   * Apply one set of changes to a selection of devices.
   *
   * Devices are named by id alone: what each one holds today is read from the
   * stored row by the API, so the browser cannot claim a device is somewhere it
   * is not. The whole batch is refused if any device is outside the caller's
   * departments, so a partial write is never the outcome.
   */
  public bulkUpdate(
    deviceIds: number[],
    patch: DeviceBulkPatch,
  ): Observable<DeviceBulkUpdateResult> {
    return this.request({
      path: 'devices/bulk',
      method: 'PATCH',
      body: { deviceIds, patch },
    });
  }

  /**
   * Departments to offer in the inline Department picker on the Devices table.
   * Same endpoint the department field on the device form pages through.
   */
  public getDepartmentDropdown(param = { limit: 100, page: 1 }): Observable<any> {
    return this.request({
      path: 'departments/dropdown',
      method: 'GET',
      query: param,
    });
  }



  /**
   * A device's shipping history — every leg it has been through, current one
   * first. Legs are never rewritten by a later shipment.
   */
  public getShipments(
    deviceId: number,
  ): Observable<{ items: DeviceShipment[] }> {
    return this.request({
      path: `devices/${deviceId}/shipments`,
      method: 'GET',
    });
  }

  /**
   * Edit the device's current shipping leg, one field at a time. Separate from
   * `patchDevice` because shipping is its own resource with its own permission.
   */
  public patchCurrentShipment(
    deviceId: number,
    data: Partial<DeviceShipment>,
  ): Observable<DeviceShipment | null> {
    return this.request({
      path: `devices/${deviceId}/shipments/current`,
      method: 'PATCH',
      body: data,
    });
  }

  /**
   * Open the device's next shipping leg. The current leg is kept as history and
   * the new one becomes what the Devices table shows.
   */
  public createShipment(
    deviceId: number,
    data: Partial<DeviceShipment>,
  ): Observable<DeviceShipment> {
    return this.request({
      path: `devices/${deviceId}/shipments`,
      method: 'POST',
      body: data,
    });
  }

  /**
   * Every issue the device has had, newest first — why it was taken out of
   * service, by whom, and when it came back. Resolved issues are never
   * rewritten, so this is the device's full trail.
   */
  public getDeviceIssues(id: number): Observable<{ items: DeviceIssue[] }> {
    return this.request({
      path: `devices/${id}/issues`,
      method: 'GET',
    });
  }

  /**
   * Take a device out of the lab's active inventory. Its status is untouched —
   * the device keeps the stage it was in and gets it back on reactivation.
   * Reason and note are both optional.
   */
  public deactivateDevice(
    id: number,
    data: DeviceDeactivatePayload = {},
  ): Observable<Device> {
    return this.request({
      path: `devices/${id}/deactivate`,
      method: 'POST',
      body: data,
    });
  }

  /**
   * Put a device back into the active inventory. The issue it was out for is
   * resolved rather than deleted, so it stays in the device's issue history.
   */
  public reactivateDevice(id: number): Observable<Device> {
    return this.request({
      path: `devices/${id}/reactivate`,
      method: 'POST',
      body: {},
    });
  }

  public create(data): Observable<any> {
    return this.request({
      path: 'devices',
      method: 'POST',
      body: data,
    });
  }

  public updateStatus(
    data: any,
    params?: Record<string, any>,
  ): Observable<any> {
    // Construct query string if params are provided
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';

    return this.request({
      path: `devices/update-status${queryString}`,
      method: 'POST',
      body: data,
    });
  }

  /**
   * One workbook for the whole Devices screen — a sheet per table (HST
   * devices, In Lab systems, In Lab equipment) over the full filtered result
   * set. Returns a signed URL, same contract as `statistics/tests/export`.
   */
  public exportAll(
    param: Record<string, any>,
  ): Observable<{ signedUrl?: string; url?: string; fileName?: string }> {
    return this.request({
      path: 'devices/export',
      method: 'GET',
      query: param,
    });
  }

  public getDeviceById(id): Observable<any> {
    return this.request({
      path: `devices/${id}`,
      method: 'GET',
    });
  }
}
